import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { socket } from '../components/socket';
import { useAuth } from './AuthContext';

const SocketContext = createContext();

export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }) => {
    const { currentUser } = useAuth();
    // const [socket, setSocket] = useState(null); // Centralizado en components/socket
    const [isConnected, setIsConnected] = useState(false);
    const [reconnectAttempts, setReconnectAttempts] = useState(0);
    const [numberChangeWarning, setNumberChangeWarning] = useState(null);

    // Configurar autenticación del socket y conectar/desconectar según el usuario
    useEffect(() => {
        let cancelled = false;
        const setupAuthAndConnect = async () => {
            try {
                if (currentUser && currentUser.getIdToken) {
                    const token = await currentUser.getIdToken();
                    if (cancelled) return;
                    socket.auth = { token };
                    if (!socket.connected) socket.connect();
                } else {
                    if (socket.connected) socket.disconnect();
                }
            } catch (error) {
                console.error('Error configurando socket:', error);
            }
        };
        setupAuthAndConnect();
        return () => { cancelled = true; };
    }, [currentUser]);

    // Listeners de conexión para exponer estado
    useEffect(() => {
        const onConnect = () => {
            setIsConnected(true);
            setReconnectAttempts(0);
        };
        const onDisconnect = (reason) => {
            setIsConnected(false);
        };
        const onConnectError = (error) => {
            setReconnectAttempts(prev => prev + 1);
        };
        
        // 🔧 NUEVO: Listener para cambio de número de WhatsApp
        const onNumberChanged = (data) => {
            console.warn('[Socket] ⚠️ Cambio de número detectado:', data);
            setNumberChangeWarning(data);
            
            // Mostrar alerta al usuario
            if (window.confirm(
                `⚠️ ADVERTENCIA: Cambio de número de WhatsApp detectado\n\n` +
                `Número anterior: ${data.previousPhone}\n` +
                `Número actual: ${data.currentPhone}\n\n` +
                `Los contactos y chats del número anterior pueden no funcionar correctamente.\n\n` +
                `¿Deseas limpiar los datos anteriores para evitar problemas?`
            )) {
                // Usuario quiere limpiar - emitir evento al backend
                socket.emit('clear_previous_number_data', { 
                    previousPhone: data.previousPhone,
                    currentPhone: data.currentPhone 
                });
            }
        };
        
        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('connect_error', onConnectError);
        socket.on('whatsapp_number_changed', onNumberChanged);
        
        return () => {
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('connect_error', onConnectError);
            socket.off('whatsapp_number_changed', onNumberChanged);
        };
    }, []);

    // Control básico de intentos de reconexión
    useEffect(() => {
        if (reconnectAttempts >= 5 && socket && socket.connected) {
            // Reiniciar la conexión si se alcanza el máximo
            socket.disconnect();
            setReconnectAttempts(0);
        }
    }, [reconnectAttempts]);

    const value = {
        socket,
        isConnected,
        reconnectAttempts
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketProvider;